import { Badge } from '@/components/ui/badge';
import {
Dialog,
DialogContent,
DialogDescription,
DialogHeader,
DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useSearch } from '@/hooks/use-search';
import { cn } from '@/utils/cn';
import { FileText,Package,Search,X } from 'lucide-react';
import { useCallback,useEffect } from 'react';

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const typeIcons = {
  item: Package,
  recipe: FileText,
};

const typeLabels = {
  item: 'Item',
  recipe: 'Recipe',
};

const typeColors = {
  item: 'text-primary',
  recipe: 'text-secondary-500',
};

export function SearchDialog({ open, onOpenChange }: SearchDialogProps) {
  const { query, results, search, selectResult, clearSearch } = useSearch();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
        clearSearch();
      }
      // Could add arrow key navigation here
    },
    [onOpenChange, clearSearch]
  );

  useEffect(() => {
    if (!open) {
      clearSearch();
    }
  }, [open, clearSearch]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 [&>button:last-child]:hidden" onKeyDown={handleKeyDown}>
        <DialogHeader className="sr-only">
          <DialogTitle>Search</DialogTitle>
          <DialogDescription>
            Search items and recipes across the workspace.
          </DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="flex items-center border-b border-border px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground mr-3" />
          <Input
            type="search"
            placeholder="Search items and recipes..."
            value={query}
            onChange={(e) => search(e.target.value)}
            className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 font-mono"
            autoFocus
          />
          {query && (
            <button
              onClick={clearSearch}
              className="p-1 hover:bg-accent rounded-none transition-colors"
              aria-label="Clear search"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto p-2">
          {results.length === 0 && query ? (
            <div className="py-12 text-center">
              <Search className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground font-mono">
                No results found for "{query}"
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="py-12 text-center">
              <Search className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground font-mono">
                Start typing to search...
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {results.map((result) => {
                const Icon = typeIcons[result.type];
                return (
                  <button
                    key={`${result.type}:${result.id}`}
                    onClick={() => {
                      selectResult(result);
                      onOpenChange(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-none hover:bg-accent transition-colors text-left group"
                  >
                    <div
                      className={cn(
                        'flex items-center justify-center h-9 w-9 bg-muted/50 shrink-0',
                        typeColors[result.type]
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-display text-sm font-medium transition-colors group-hover:text-primary" title={result.title}>
                        {result.title}
                      </p>
                      <p className="truncate font-mono text-xs text-muted-foreground" title={result.subtitle}>
                        {result.subtitle}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className="shrink-0 font-mono text-[10px] uppercase"
                    >
                      {typeLabels[result.type]}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-4 py-2 flex items-center justify-between text-xs text-muted-foreground font-mono bg-muted/20">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded-none">
                ↑↓
              </kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded-none">
                Enter
              </kbd>
              Select
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded-none">
              Esc
            </kbd>
            Close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
